import { useState } from 'react'
import {
  Icon,
  Text,
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@flatfile/baseline'
import { useEnrichmentStatus } from '../../hooks/useEnrichmentStatus'
import { Loader2 } from 'lucide-react'

interface EnrichmentStatusIndicatorProps {
  sheetId?: string
  className?: string
  onEnrichmentClick?: (enrichmentKey: string) => void
}

export function EnrichmentStatusIndicator({
  sheetId,
  className,
  onEnrichmentClick,
}: EnrichmentStatusIndicatorProps) {
  const [open, setOpen] = useState(false)
  const { status, loading, hasRunningEnrichments, runningCount, failedCount } =
    useEnrichmentStatus(sheetId)

  if (!sheetId || loading || !status) {
    return null
  }

  // Don't show indicator if no enrichments configured
  if (status.summary.total === 0) {
    return null
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className={cn(
            'relative h-8 px-2 gap-1',
            hasRunningEnrichments && 'animate-pulse',
            className
          )}
        >
          <Icon
            name='lightning'
            className={cn(
              'h-4 w-4',
              hasRunningEnrichments && 'text-primary',
              failedCount > 0 && 'text-destructive'
            )}
          />
          {hasRunningEnrichments && (
            <Loader2 className='h-3 w-3 animate-spin' />
          )}
          {(runningCount > 0 || failedCount > 0) && (
            <Badge
              variant={failedCount > 0 ? 'destructive' : 'default'}
              className='px-1 h-4 min-w-[16px] text-[10px]'
            >
              {failedCount > 0 ? failedCount : runningCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-80 p-0'>
        <div className='p-4 space-y-4'>
          <div className='flex items-center justify-between'>
            <Text className='text-sm font-semibold'>Enrichment Status</Text>
            {hasRunningEnrichments && (
              <Badge variant='secondary' className='gap-1.5 px-2'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span className='text-xs'>Running</span>
              </Badge>
            )}
          </div>

          <div className='space-y-3'>
            {status.enrichments.map((enrichment) => (
              <div
                key={enrichment.id}
                className='flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-muted/50'
              >
                <div className='flex-1 min-w-0 space-y-1'>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-auto p-0 text-left justify-start font-medium text-sm truncate hover:text-primary'
                    onClick={() => {
                      if (onEnrichmentClick) {
                        onEnrichmentClick(enrichment.key)
                        setOpen(false)
                      }
                    }}
                  >
                    {enrichment.name || enrichment.key}
                  </Button>
                  {enrichment.status === 'running' && (
                    <Text className='text-xs text-muted-foreground'>
                      {enrichment.recordsProcessed || 0} of{' '}
                      {enrichment.totalRecords || 0} records processed
                    </Text>
                  )}
                </div>
                <div className='ml-3 flex-shrink-0'>
                  <StatusBadge status={enrichment.status} />
                </div>
              </div>
            ))}
          </div>

          <div className='pt-4 border-t border-muted/50'>
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <Text className='text-sm font-medium text-muted-foreground'>
                  Total
                </Text>
                <Text className='text-lg font-bold'>
                  {status.summary.total}
                </Text>
              </div>
              <div className='flex items-center justify-between'>
                <Text className='text-sm font-medium text-muted-foreground'>
                  Background
                </Text>
                <Text className='text-lg font-bold'>
                  {status.summary.backgroundEnabled}
                </Text>
              </div>
              {status.summary.running > 0 && (
                <div className='flex items-center justify-between'>
                  <Text className='text-sm font-medium text-muted-foreground'>
                    Running
                  </Text>
                  <Text className='text-lg font-bold text-primary'>
                    {status.summary.running}
                  </Text>
                </div>
              )}
              {status.summary.failed > 0 && (
                <div className='flex items-center justify-between'>
                  <Text className='text-sm font-medium text-muted-foreground'>
                    Failed
                  </Text>
                  <Text className='text-lg font-bold text-destructive'>
                    {status.summary.failed}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'running':
      return (
        <Badge variant='secondary' className='gap-1'>
          <Loader2 className='h-3 w-3 animate-spin' />
          Running
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant='outline' className='gap-1 text-green-600'>
          <Icon name='checkmark-2' className='h-3 w-3' />
          Done
        </Badge>
      )
    case 'failed':
      return (
        <Badge variant='destructive' className='gap-1'>
          <Icon name='x' className='h-3 w-3' />
          Failed
        </Badge>
      )
    case 'ready':
    default:
      return (
        <Badge variant='outline' className='text-muted-foreground'>
          Ready
        </Badge>
      )
  }
}
